import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import AboutContent from './AboutContent';

const MODAL_WIDTH = 585;

const c = {
  text: '#1e293b',
  textMuted: '#64748b',
  border: '#e2e8f0',
  surfaceAlt: '#f8fafc',
};

/**
 * "About" footer for the login / activation screens.
 * Opens the same content as Settings → About in a popup modal.
 */
export default function AboutFooter() {
  const { t, isRTL } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <>
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ marginTop: 14 }}>
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              border: `1px solid ${c.border}`,
              background: 'rgba(255,255,255,0.8)',
              color: c.textMuted,
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'color 0.15s, background 0.15s, border-color 0.15s',
            }}
          >
            <Info size={13} style={{ flexShrink: 0 }} />
            <span>{t('settings.about')} — {t('settings.aboutAppName')}</span>
          </button>
        </div>
      </div>

      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="about-modal-title"
          dir={isRTL ? 'rtl' : 'ltr'}
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16,
          }}
        >
          <div
            className="animate-fade-up"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              border: `1px solid ${c.border}`,
              borderRadius: 16,
              width: MODAL_WIDTH,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 12px 48px rgba(15, 23, 42, 0.14)',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '16px 20px',
              borderBottom: `1px solid ${c.border}`,
            }}>
              <div id="about-modal-title" style={{ fontSize: 15, fontWeight: 600, color: c.text }}>
                {t('settings.about')}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('common.close')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  border: 'none',
                  background: c.surfaceAlt,
                  color: c.textMuted,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <X size={16} />
              </button>
            </div>

            <AboutContent />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
